/**
 * The short texts the run page shows: a subject's head, a one-line gist, a duration.
 *
 * @since 1.0.0
 */

const GIST_MAX = 160;

/**
 * A subject (a commit sha, `pr12@sha2`) as a bar label or row meta: its first 8 chars.
 *
 * @since 1.0.0
 */
export const shortSubject = (subject: string): string => subject.slice(0, 8);

/**
 * A value on one line: a string as is, anything else as compact JSON; whitespace
 * collapsed and clipped to 160 chars.
 *
 * @since 1.0.0
 */
export const gist = (value: unknown): string => {
  const raw = typeof value === "string" ? value : (JSON.stringify(value) as string | undefined) ?? "";
  const line = raw.replace(/\s+/g, " ").trim();
  return line.length > GIST_MAX ? `${line.slice(0, GIST_MAX - 1)}…` : line;
};

/**
 * A duration as `42s`, `3m 27s` or `1h 5m`.
 *
 * @since 1.0.0
 */
export const duration = (ms: number): string => {
  const s = Math.floor(Math.max(0, ms) / 1000);
  return s < 60
    ? `${s}s`
    : s < 3600
    ? `${Math.floor(s / 60)}m ${s % 60}s`
    : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

/**
 * An optional field to spread into a view: the key with its value when there is one, else
 * nothing — so a view never carries a key set to `undefined`.
 *
 * @since 1.0.0
 */
export const optional = <K extends string, V>(key: K, value: V | undefined): { readonly [P in K]?: V } =>
  value === undefined ? {} : ({ [key]: value } as { readonly [P in K]?: V });

/**
 * An artifact as the view carries it: its url only when it has one.
 *
 * @since 1.0.0
 */
export const artifactView = (
  artifact: { readonly kind: string; readonly id: string; readonly url?: string | undefined },
): { readonly kind: string; readonly id: string; readonly url?: string } => ({
  kind: artifact.kind,
  id: artifact.id,
  ...optional("url", artifact.url),
});
